import { canonTag, resolveTag, tagIndex, TAG_KIND_LABELS } from './tags.js';
import { isImageMime } from './media.js';
import { deptRole, goalsFor } from './deptgoals.js';
import { tagTime, tagProjectRows } from './projecttags.js';

// Where a tag is used (Lj, Oct 11: "Tags have a database but no way to reach them in the UI"): the tag browse
// (phone Tag screen, web /tag/:slug) and the phone's Tags page read these. Every tag is read through the suite list
// (resolveTag → canonTag), so #Bug, #bug and a tag merged into #bug land on one page. Pure.

/** The tags a department's score is built on, by department role (deptengine.js shipLine/devEngine, fieldgoals.js). */
export const SCORE_TAG_ROLES = Object.freeze({
  bug: 'dev', shipped: 'dev', improvement: 'dev', deploy: 'dev',
  callback: 'field', materials: 'field', travel: 'field',
});

/** A tag's key on the tag pages: the suite's slug after merges. */
export const tagSlug = (t, opsTags) => (t ? canonTag(resolveTag(t, tagIndex(opsTags))) : '');

// Files saved before free tags had one `kind`; it reads as the matching tag (mobile ProjectFiles.fileTags).
const LEGACY_KIND_TAG = { drawing: 'drawings', estimate: 'estimate', permit: 'permits', reference: 'reference' };
export const fileTagList = (f) => (Array.isArray(f?.tags) ? f.tags : (LEGACY_KIND_TAG[f?.kind] ? [LEGACY_KIND_TAG[f.kind]] : []));

const isDone = (t) => t.status === 'done' || t.status === 'skipped';
const num = (v) => (typeof v === 'number' ? v : Number(v) || 0);
const slugsOf = (tags, idx) => {
  const out = new Set();
  for (const t of Array.isArray(tags) ? tags : []) { const k = t ? canonTag(resolveTag(t, idx)) : ''; if (k) out.add(k); }
  return out;
};
const liveTask = (t) => t && !t.deletedAt;
const liveNote = (n) => n && !n.deletedAt && n.kind !== 'template';
const liveFile = (f) => f && !f.deletedAt;

/**
 * Everything carrying one tag: { slug, doc, kind, kindLabel, inList, projects, time, tasks: { open, done }, notes, photos,
 * files, counts: { open, done, notes, photos, files, projects, total } }. Open tasks: dated first (soonest), then newest;
 * done: newest finish first; notes, photos and other files newest first. Deleted items, trashed notes and note
 * templates are left out. Projects (v0.1.259): the projects carrying the tag (projecttags.tagProjectRows: open tasks,
 * hours, status), `time` = projecttags.tagTime (task-tag time vs "via project tag", this month / all time, 30 days).
 */
export function tagUsage(tag, { tasks = [], notes = [], files = [], tags: opsTags = [], projects = [], today } = {}) {
  const idx = tagIndex(opsTags);
  const slug = tagSlug(tag, idx);
  const doc = slug ? idx.get(slug) || null : null;
  const carries = (list) => slugsOf(list, idx).has(slug);
  const open = []; const done = [];
  for (const t of tasks || []) if (liveTask(t) && carries(t.tags)) (isDone(t) ? done : open).push(t);
  open.sort((a, b) => (a.scheduledDate ? 0 : 1) - (b.scheduledDate ? 0 : 1) || String(a.scheduledDate || '').localeCompare(String(b.scheduledDate || '')) || num(b.createdAt) - num(a.createdAt));
  done.sort((a, b) => num(b.completedAt) - num(a.completedAt));
  const noteList = (notes || []).filter((n) => liveNote(n) && carries(n.tags)).sort((a, b) => num(b.createdAt) - num(a.createdAt));
  const tagged = (files || []).filter((f) => liveFile(f) && carries(fileTagList(f))).sort((a, b) => num(b.createdAt) - num(a.createdAt));
  const photos = tagged.filter((f) => isImageMime(f.mime));
  const other = tagged.filter((f) => !isImageMime(f.mime));
  const kind = doc?.kind || null;
  const timeSrc = { tasks, projects, tags: idx, ...(today ? { today } : {}) };
  const tt = tagTime(slug, timeSrc);
  const projectRows = slug ? tagProjectRows(slug, timeSrc, tt) : [];
  const { byProject, ...time } = tt;
  return {
    slug, doc, kind, kindLabel: kind ? TAG_KIND_LABELS[kind] || kind : null, inList: !!doc,
    projects: projectRows, time,
    tasks: { open, done }, notes: noteList, photos, files: other,
    counts: { open: open.length, done: done.length, notes: noteList.length, photos: photos.length, files: other.length, projects: projectRows.length, total: open.length + done.length + noteList.length + tagged.length + projectRows.length },
  };
}

/**
 * The Tags page: every live tag in the suite list with its counts here, and the tags used here that aren't in the
 * list (strays). { list: [{ slug, doc, kind, kindLabel, counts }], strays: [{ slug, counts }], merged: [{ slug, into }] }.
 * Both lists: most used first, then by name.
 */
export function tagCounts({ tasks = [], notes = [], files = [], tags: opsTags = [], projects = [] } = {}) {
  const idx = tagIndex(opsTags);
  const m = new Map();
  const bump = (slug, k) => {
    let c = m.get(slug);
    if (!c) { c = { open: 0, done: 0, notes: 0, photos: 0, files: 0, projects: 0, total: 0 }; m.set(slug, c); }
    c[k] += 1; c.total += 1;
  };
  for (const p of projects || []) if (p && !p.deletedAt) for (const k of slugsOf(p.tags, idx)) bump(k, 'projects');
  for (const t of tasks || []) if (liveTask(t)) for (const k of slugsOf(t.tags, idx)) bump(k, isDone(t) ? 'done' : 'open');
  for (const n of notes || []) if (liveNote(n)) for (const k of slugsOf(n.tags, idx)) bump(k, 'notes');
  for (const f of files || []) if (liveFile(f)) for (const k of slugsOf(fileTagList(f), idx)) bump(k, isImageMime(f.mime) ? 'photos' : 'files');
  const zero = { open: 0, done: 0, notes: 0, photos: 0, files: 0, projects: 0, total: 0 };
  const order = (a, b) => b.counts.total - a.counts.total || a.slug.localeCompare(b.slug);
  const list = []; const merged = [];
  for (const [slug, doc] of idx) {
    if (doc.mergedInto) { merged.push({ slug, into: doc.mergedInto }); continue; }
    list.push({ slug, doc, kind: doc.kind || null, kindLabel: doc.kind ? TAG_KIND_LABELS[doc.kind] || doc.kind : null, counts: m.get(slug) || { ...zero } });
  }
  const strays = [...m.entries()].filter(([slug]) => !idx.has(slug)).map(([slug, counts]) => ({ slug, counts }));
  return { list: list.sort(order), strays: strays.sort(order), merged: merged.sort((a, b) => a.slug.localeCompare(b.slug)) };
}

/** "1 project · 3 open · 12 done · 4 notes · 2 photos" — only what's there; "Not used here yet" when nothing. */
export function tagCountsText(c) {
  if (!c || !c.total) return 'Not used here yet';
  return [
    c.projects ? `${c.projects} project${c.projects === 1 ? '' : 's'}` : null,
    c.open ? `${c.open} open` : null, c.done ? `${c.done} done` : null,
    c.notes ? `${c.notes} note${c.notes === 1 ? '' : 's'}` : null,
    c.photos ? `${c.photos} photo${c.photos === 1 ? '' : 's'}` : null,
    c.files ? `${c.files} file${c.files === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
}

/**
 * Search: the tags matching typed text ("#oli", "olivers"), from tagCounts — used ones only, a prefix match first,
 * then most used. → [{ slug, kind, kindLabel, counts }], at most `n`.
 */
export function findTags(q, counts, n = 4) {
  const k = canonTag(q);
  if (!k || !counts) return [];
  const all = [...(counts.list || []), ...(counts.strays || [])].filter((t) => t.counts?.total > 0 && t.slug.includes(k));
  return all.sort((a, b) => (b.slug.startsWith(k) - a.slug.startsWith(k)) || b.counts.total - a.counts.total || a.slug.localeCompare(b.slug))
    .slice(0, n).map((t) => ({ slug: t.slug, kind: t.kind || null, kindLabel: t.kindLabel || null, counts: t.counts }));
}

const ROLE_NAME = { dev: 'Dev', field: 'Field' };

/**
 * The department scores a tag counts for: [{ id, name, color }]. The fixed score tags go to the department of their
 * role (its own name), plus any department with a tag:<slug> / open:<slug> goal. A fixed tag with no such department
 * yet still names the role (id null).
 */
export function tagScoreDepts(tag, departments = []) {
  const slug = canonTag(tag);
  if (!slug) return [];
  const out = [];
  const seen = new Set();
  const role = SCORE_TAG_ROLES[slug] || null;
  for (const d of departments || []) {
    if (!d || d.deletedAt || d.archived) continue;
    const byRole = role && deptRole(d) === role;
    const byGoal = goalsFor(d).some((g) => g.key === `tag:${slug}` || g.key === `open:${slug}`);
    if ((byRole || byGoal) && !seen.has(d.id)) { seen.add(d.id); out.push({ id: d.id, name: d.name, color: d.color || null }); }
  }
  if (role && !out.some((d) => deptRole(d) === role)) out.push({ id: null, name: ROLE_NAME[role], color: null });
  return out;
}

/** Each tag kind's colour on the tag pages (phone + web), so a kind reads the same in both apps. */
export const TAG_KIND_COLORS = Object.freeze({ trade: '#FFD23F', work: '#FB5446', internal: '#9FB6CC', channel: '#FF3DD8', equipment: '#00E1FF', personal: '#00F5A0' });
export const tagKindColor = (kind) => TAG_KIND_COLORS[kind] || '#A0958A';
